import React, { useState, useEffect, useRef } from 'react';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps,
} from '@material-ui/pickers';
import { withStyles } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import ClearIcon from '@material-ui/icons/Clear';
import styled from 'styled-components';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { ExtendMui } from '@material-ui/pickers/typings/extendMui';
import { format, isValid } from 'date-fns';
import { dDaFormat, dVariant } from 'main_consts';

// onchange example----------------------------------------------------
// export const DatePickerChange = UpdateData => (name, dateFormat: (IdateFormat | string[]) = dDaFormat) => date => {
//     if (isArray(name) && isArray(dateFormat) && name.length === dateFormat.length) {
//         const updateObj = {};
//         name.forEach((obj, idx) => updateObj[name[idx]] = daFormatTrans(date, dateFormat[idx] as IdateFormat))
//         UpdateData(updateObj)
//     }
//     else {
//         UpdateData({ [name]: daFormatTrans(date, dateFormat) });
//     }
//     return null
// }

// const----------------------------------------------------
export const EmptyDateMsg = '請填寫這個欄位。';
export const dateErrStr = '起始日期不得大於結束日期。';

export type IupdateObj<T = unknown> = Record<string, T>;

interface ICustomProps {
  required?: boolean;
  verifyBeforeChange?: boolean;
  verifiers?: Iverifier[];
}

interface Iverifier {
  func: (value: ParsableDate) => boolean;
  text: string;
}
// style----------------------------------------------------
const StyleSpan = styled.span`
  display: flex;
  color: rgba(0, 0, 0, 0.54);
  border-radius: 50%;
  padding: 0.5em;
  position: absolute;
  top: 0;
  right: 5px;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  :hover {
    background-color: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.8);
  }
`;

// html required 無效，因為 input readonly
const StyleDatePicker = withStyles({
  root: {
    '& .MuiInputBase-formControl': {
      padding: 0,
      '& input': {
        padding: '0.675em 2.1em 0.675em 0.75em',
        // fontSize: '1.15em',
      },
      '& input:disabled': {
        padding: '0.675em 0.75em',
      },
    },
  },
})(MuiDatePicker);
interface IDatePicker
  extends Omit<ExtendMui<DatePickerProps>, 'onClose'>,
    ICustomProps {
  onClose?: (date: Date) => void;
}
const DatePicker = (props: IDatePicker): JSX.Element => {
  const {
    value,
    onChange,
    onClose,
    error,
    helperText,
    verifiers = [],
    verifyBeforeChange = false,
    required,
    className,
    disabled,
    format: dateFormat = dDaFormat,
    minDate,
    maxDate,
    ...other
  } = props;
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [FirstVerify, setFirstVerify] = useState(verifyBeforeChange);

  const dateRef = useRef(value);
  useEffect(() => {
    dateRef.current = value;
  }, [value]);

  if (required) {
    verifiers.push({ func: (d) => !d, text: EmptyDateMsg });
  }

  useEffect(() => {
    // 始設畫面預設不 verify , 除非 verifyBeforeChange = true
    if (!FirstVerify) {
      setFirstVerify(true);
      setMessage('');
      return;
    }

    // 驗使用者設定之 verifier, 空值也要驗(不能驗 HTML validation)
    // input 為 readonly or hidden 時，不會觸發
    if (verifiers.length > 0) {
      const veriObj = verifiers.find((obj) => obj.func(value));
      if (veriObj) {
        setMessage(veriObj.text);
        return;
      }
    }

    // 先前檢驗都 pass
    setMessage('');
  }, [value, verifyBeforeChange]);

  const LocalPicker = (
    <StyleDatePicker
      value={value}
      style={{ width: '100%' }}
      autoOk
      variant="inline"
      inputVariant={dVariant}
      error={error || message.length > 0}
      helperText={message || helperText}
      open={open}
      onChange={(d) => {
        dateRef.current = d;
        if (onChange) {
          onChange(d);
        }
      }}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        if (onClose) {
          onClose(dateRef.current as Date);
        }
      }}
      format={dateFormat}
      minDate={minDate}
      maxDate={maxDate}
      invalidDateMessage="無效的日期格式"
      minDateMessage={`日期不得小於${
        isValid(minDate)
          ? ` ${format(minDate as Date, dateFormat)}`
          : '最小日期'
      }`}
      maxDateMessage={`日期不得大於${
        isValid(maxDate)
          ? ` ${format(maxDate as Date, dateFormat)}`
          : '最大日期'
      }`}
      {...other}
    />
  );

  const chooseIcon = !value ? (
    <label style={{ position: 'relative', width: '100%', display: 'flex' }}>
      {LocalPicker}
      <StyleSpan>
        <EventIcon />
      </StyleSpan>
    </label>
  ) : (
    <div style={{ position: 'relative', width: '100%', display: 'flex' }}>
      {LocalPicker}
      <StyleSpan onClick={onChange ? () => onChange(null) : undefined}>
        <ClearIcon />
      </StyleSpan>
    </div>
  );

  return (
    <div className={className} style={{ width: '100%' }}>
      {disabled ? (
        <StyleDatePicker
          value={value}
          disabled={disabled}
          style={{ width: '100%' }}
          autoOk
          variant="inline"
          inputVariant={dVariant}
          onChange={() => undefined}
          format={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          invalidDateMessage="無效的日期格式"
          minDateMessage={`日期不得小於 ${format(minDate as Date, dateFormat)}`}
          maxDateMessage={`日期不得大於 ${format(maxDate as Date, dateFormat)}`}
          {...other}
        />
      ) : (
        chooseIcon
      )}
    </div>
  );
};

export default DatePicker;
